import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

import { PageHeader } from "@/client/components/page-header";
import { Badge } from "@/client/components/ui/badge";
import { Button } from "@/client/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/client/components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/client/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/client/components/ui/form";
import { useAuthStore } from "@/client/features/auth/auth-store";
import { storageApi } from "@/client/features/storage/storage-api";
import { ApiError } from "@/client/lib/api";
import { NotFound } from "@/client/pages/not-found";
import {
  DEFAULT_STORAGE_TIER,
  STORAGE_TIER_IDS,
  STORAGE_TIERS,
  storageTier,
  tierCapacityLabel,
} from "@/shared/storage-tiers";

const textareaClass =
  "border-input bg-transparent focus-visible:border-ring focus-visible:ring-ring/50 min-h-24 w-full rounded-md border px-3 py-2 text-sm shadow-xs focus-visible:ring-[3px] focus-visible:outline-none";
const selectClass =
  "border-input bg-transparent focus-visible:border-ring focus-visible:ring-ring/50 h-9 w-full rounded-md border px-3 text-sm shadow-xs focus-visible:ring-[3px] focus-visible:outline-none";

const requestSchema = z.object({
  tier: z.enum(STORAGE_TIER_IDS),
  notes: z.string().trim().max(2000, "Keep notes under 2000 characters.").optional(),
});

type RequestValues = z.input<typeof requestSchema>;

function TierCards() {
  return (
    <div className="grid gap-4 md:grid-cols-3">
      {STORAGE_TIERS.map((tier) => (
        <Card key={tier.id} className="flex flex-col">
          <CardHeader>
            <div className="flex items-center gap-2">
              <CardTitle>{tier.name}</CardTitle>
              {tier.free ? (
                <Badge variant="secondary" className="font-normal">
                  Free
                </Badge>
              ) : null}
            </div>
            <CardDescription>{tierCapacityLabel(tier)}</CardDescription>
          </CardHeader>
          <CardContent className="text-sm">
            <dl className="grid gap-2">
              <div>
                <dt className="text-muted-foreground text-xs">Best for</dt>
                <dd>{tier.bestFor}</dd>
              </div>
            </dl>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function RequestDialog() {
  const [open, setOpen] = useState(false);
  const form = useForm<RequestValues>({
    resolver: zodResolver(requestSchema),
    defaultValues: { tier: DEFAULT_STORAGE_TIER, notes: "" },
  });

  const selectedTier = storageTier(form.watch("tier"));

  const onSubmit = async (values: RequestValues) => {
    try {
      const parsed = requestSchema.parse(values);
      const ticket = await storageApi.request({
        tier: parsed.tier,
        notes: parsed.notes?.trim() || undefined,
      });
      toast.success(`Storage request ${ticket.identifier} submitted. Track it under Support.`);
      form.reset({ tier: DEFAULT_STORAGE_TIER, notes: "" });
      setOpen(false);
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : "Unable to submit the request. Please try again.";
      toast.error(message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>Request storage</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Request storage</DialogTitle>
          <DialogDescription>
            Storage isn't provisioned by default, so every tier is set up on request. Pick one and
            our team will follow up. You can track it under Support.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-4">
            <FormField
              control={form.control}
              name="tier"
              render={({ field }) => (
                <FormItem className="sm:max-w-xs">
                  <FormLabel>Tier</FormLabel>
                  <FormControl>
                    <select className={selectClass} {...field}>
                      {STORAGE_TIERS.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name} — {t.capacityTiB} TiB{t.free ? " (free)" : ""}
                        </option>
                      ))}
                    </select>
                  </FormControl>
                  <FormDescription>
                    {selectedTier.commercial
                      ? "Commercial tier. After you submit, our team will follow up to discuss terms."
                      : "Free of charge. Submit a request and we'll set it up."}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes (optional)</FormLabel>
                  <FormControl>
                    <textarea
                      className={textareaClass}
                      placeholder="Workload, protocol preference (NFS or S3), timeline, or anything that helps us plan."
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline">
                  Cancel
                </Button>
              </DialogClose>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? "Submitting…" : "Submit request"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export function StoragePage() {
  const storageEnabled = useAuthStore((s) => s.features.storage);
  const ticketsEnabled = useAuthStore((s) => s.features.tickets);
  // Requests are filed as support tickets and view-as sessions are read-only, so
  // only offer the action when it can actually succeed.
  const impersonating = useAuthStore((s) => s.impersonation != null);
  const deactivated = useAuthStore((s) => s.deactivated);
  const canRequest = ticketsEnabled && !impersonating && !deactivated;

  // The nav hides Storage for unentitled tenants and the API 404s the whole
  // /storage subtree; match that here so a direct URL visit doesn't render the
  // page (and a request that would just fail server-side).
  if (!storageEnabled) {
    return <NotFound />;
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Storage"
        description="High-performance file and object storage for your datasets, checkpoints, and scratch space."
      >
        {canRequest ? <RequestDialog /> : null}
      </PageHeader>

      <TierCards />

      <Card>
        <CardHeader>
          <CardTitle>How it works</CardTitle>
          <CardDescription>
            Mantle storage is powered by{" "}
            <a
              href="https://www.vastdata.com/"
              target="_blank"
              rel="noreferrer"
              className="text-foreground underline underline-offset-4"
            >
              VAST Data
            </a>{" "}
            and includes NFS, S3, and plain HTTP access. Provisioning isn't automated yet, so
            requests are handled by the Mantle team.
          </CardDescription>
        </CardHeader>
        <CardContent className="text-muted-foreground text-sm">
          {canRequest ? (
            <p>
              Storage isn't provisioned by default: every tier is set up on request. Standard (10
              TiB) is free of charge. For Performance and Extreme, our team will reach out after
              your request to discuss commercial terms. Submit a request above and track it on the{" "}
              <Link to="/tickets" className="text-foreground underline underline-offset-4">
                Support
              </Link>{" "}
              page.
            </p>
          ) : impersonating ? (
            <p>Storage requests are disabled while viewing as a tenant.</p>
          ) : deactivated ? (
            <p>
              Storage requests are disabled because this account is deactivated. Contact your Mantle
              account team to reactivate.
            </p>
          ) : (
            <p>Storage requests are handled through Support, which isn't available right now.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
